/**
 * A request from the client for the silo reader to capture right now.
 *
 * The browser cannot call the reader directly — prism-client is served over HTTPS and the
 * service is plain HTTP on a LAN address, which browsers block as mixed content, and the
 * service's API token has no business being in a browser anyway. But both sides can reach
 * Appwrite, so Appwrite carries the request: the client stamps this row, the service polls
 * for it, runs the capture, and writes the outcome back. The client watches the same row and
 * sees it move through the states live.
 *
 * There is exactly ONE row, `RESIN_SILO_CAPTURE_REQUEST_ID`. A queue would let a stack of
 * requests build up while the reader is busy, which is not what anyone wants from a "capture
 * now" button — the newest request replaces the old one and the row doubles as the status
 * display.
 */
export enum ResinSiloCaptureRequestStatus {
    /** Nothing outstanding. The last run, if any, is described by `message`. */
    Idle = 'idle',
    /** The client asked. The service picks this up on its next poll. */
    Requested = 'requested',
    /** The service claimed it and is talking to the camera. */
    Running = 'running',
    /** Finished, values in `message`, row id in `readingId`. */
    Complete = 'complete',
    /** Finished with nothing stored. `message` says why. */
    Failed = 'failed',
}

export interface ResinSiloCaptureRequest {
    id: string;
    status: ResinSiloCaptureRequestStatus;
    /** When the button was pressed. Also how the service tells a new request from the old one. */
    requestedOn: Date;
    /** Who pressed it, for the status line. */
    requestedByName: string;
    /** When the service finished. Null while requested or running. */
    completedOn: Date | null;
    /** Human-readable outcome: the two values, or why there are none. */
    message: string;
    /** The resin-silo-reading row this produced, when it produced one. */
    readingId: string;
}

/** The single row every client and the service share. */
export const RESIN_SILO_CAPTURE_REQUEST_ID = 'current';

export function createEmptyResinSiloCaptureRequest(args: {
    status?: ResinSiloCaptureRequestStatus;
    requestedOn?: Date;
    requestedByName?: string;
    completedOn?: Date | null;
    message?: string;
    readingId?: string;
}): ResinSiloCaptureRequest {
    return {
        id: RESIN_SILO_CAPTURE_REQUEST_ID,
        status: args.status ?? ResinSiloCaptureRequestStatus.Idle,
        requestedOn: args.requestedOn ?? new Date(),
        requestedByName: args.requestedByName ?? '',
        completedOn: args.completedOn ?? null,
        message: args.message ?? '',
        readingId: args.readingId ?? '',
    };
}

/** True while the reader owes an answer — the button stays disabled and the spinner runs. */
export function resinSiloCaptureRequestIsPending(request: ResinSiloCaptureRequest | undefined): boolean {
    return request?.status === ResinSiloCaptureRequestStatus.Requested
        || request?.status === ResinSiloCaptureRequestStatus.Running;
}

/**
 * A request the service should act on.
 *
 * `maxAgeMs` guards the case where the reader was down when the button was pressed: coming
 * back up hours later and photographing the silos because of a stale click is worse than
 * doing nothing. Anything older is ignored.
 */
export function resinSiloCaptureRequestIsActionable(
    request: ResinSiloCaptureRequest | undefined,
    now: Date,
    maxAgeMs: number,
): boolean {
    if (request?.status !== ResinSiloCaptureRequestStatus.Requested) return false;
    return now.getTime() - new Date(request.requestedOn).getTime() <= maxAgeMs;
}
